import { formatCellDisplayValue } from "@common/controls/Grid/lib";
import { type SortInfo } from "@common/declarations";
import { sort } from "@common/lib/array";
import { groupBy, toPairs } from "lodash";
import { type GridColumn, type GridGroupInfo } from "../Grid/types";
import { type GridViewGroupBy, type GridViewGroupingInfo, type GridViewToolbar } from "./types";

const adjustCsvValue = (val: any) => {
  if (!val && val !== 0) return "";
  const sv = val.toString().replace(/"/, '""');
  if (sv.includes(",")) return `"${sv}"`;
  return sv;
};
export const toCsv = <T extends {}>(columns: GridColumn<T>[], data: any[]): string =>
  [
    columns.map((x) => adjustCsvValue(x.name)).join(","),
    ...data.map((x) =>
      columns.map((y) => adjustCsvValue(formatCellDisplayValue(y.data ? y.data(x) : x[y.key], y.dataType))).join(",")
    )
  ].join("\n");

export const groupData = <T>(
  data: T[],
  idKey: keyof T,
  grouping?: GridViewGroupingInfo<T>,
  sortedBy?: SortInfo<T>
): Array<T | GridGroupInfo> => {
  if (!grouping || !data.length) {
    return data;
  }

  if (typeof grouping.groupBy === "string") {
    const groupByProp = grouping.groupBy;
    let pairs = toPairs(groupBy(data, (x) => x[groupByProp]));
    if (grouping.sort) {
      pairs = pairs.sort((a, b) => a[0].localeCompare(b[0]) * (grouping.sort === "desc" ? -1 : 1));
    }
    return pairs.reduce(
      (res, p) => [
        ...res,
        {
          id: `group_${p[0]}`,
          label: p[0],
          value: p[1][0][groupByProp]
        } as GridGroupInfo<T>,
        ...sort(p[1] as T[], sortedBy)
      ],
      []
    );
  }

  const dataMap = data.reduce((res, x) => ({ ...res, [x[idKey] as any]: x }), {});
  return (grouping.groupBy as GridViewGroupBy[]).reduce(
    (res, x) => [
      ...res,
      {
        id: x.id,
        label: x.label,
        value: x.childIds.find((id) => id in dataMap) ? x.childIds.find((id) => id in dataMap)[idKey] : null
      } as GridGroupInfo<T>,
      ...sort(
        x.childIds.filter((id) => id in dataMap).map((id) => dataMap[id]),
        sortedBy
      )
    ],
    []
  );
};

export const NO_TOOLBAR: GridViewToolbar<any> = {
  showAdd: false,
  showSearch: false
};
